import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';
import { useDispatch, useSelector } from 'react-redux';
import { selectIncident } from 'slices/commonSelectors';
import {
  addMemberToTeams,
  RemoveUserFromWorkspaceTeamSite,
} from 'actions/teamsActions';

// Query keys
export const keys = {
  incidentRoster: 'incidentRoster',
  groupRosters: 'groupRosters',
  incidentSchedule: 'incidentSchedule',
  userIncidentSchedule: 'userIncidentSchedule',
};

// Types
interface RootState {
  app: {
    user: {
      user_guid: string;
    };
  };
}

interface IncidentMember {
  user_guid: string;
  email_address: string;
  incident_member_id?: string;
  [key: string]: any;
}

interface AddUserToIncidentParams {
  incidentMembers: IncidentMember[];
  memberEmails: string[];
}

interface EditIncidentMemberParams {
  member: IncidentMember;
  incident_member_id: string;
}

interface MarkIncidentUserActiveParams {
  user_guid: string;
  incident_member_id: string;
}

interface AddNonMemberToIncidentParams {
  incidentNonMember: any;
  incident_id: string;
}

export interface TeamMembership {
  name: string;
}

export interface ProfileSettings {
  is_new_user: string;
  userName: string;
  mobile_phone_number: string;
  home_phone_number: string;
  pager_address: string;
  opt_in_for_sms: boolean;
}

export interface IncidentRosterMember {
  incident_member_id: string;
  incident_id: string;
  user_guid: string;
  role: string;
  active: number;
  exited: number;
  seat: string | null;
  incident_seat: string;
  active_time: string | null;
  minutes_worked: string;
  seat_name: string | null;
  seat_description: string | null;
  email_address: string;
  profile_settings: ProfileSettings;
  user_name: string;
  allow_admin_profile_edits: boolean;
  relevant_team_memberships: TeamMembership[];
}

export interface RosterItem {
  user_guid: string;
  email_address: string;
  subscription_id: string;
  currently_selected: any;
  profile_settings: ProfileSettings;
  user_name: string;
  group_guid: string;
  group_name: string;
  is_closed: boolean | null;
  default_settings: any;
  default_sender_email: null | string;
  risk_levels: string[];
  daily_digest_team: string;
  allow_admin_profile_edits: boolean;
  role_assignments: { name: string }[];
  unavailability: any[];
  seats: any[];
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useGetRosterForIncident = (
  incidentId: string
): UseQueryResult<IncidentRosterMember[], Error> => {
  return useQuery<IncidentRosterMember[], Error>({
    queryKey: [keys.incidentRoster, incidentId],
    queryFn: async () => {
      if (!incidentId) return [];
      const response = await axios(config.apiGateway.getRosterForIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
        },
      });
      return response.data || [];
    },
    enabled: !!incidentId,
  });
};

export const useGetRostersForGroup = (
  groupGuid: string
): UseQueryResult<RosterItem[], Error> => {
  const { user_guid } = useSelector((state: RootState) => state.app.user);

  return useQuery<RosterItem[], Error>({
    queryKey: [keys.groupRosters, groupGuid],
    queryFn: async () => {
      if (!groupGuid) return [];
      const response = await axios(config.apiGateway.fetchRostersForGroups, {
        method: 'POST',
        data: {
          group_guids: [groupGuid],
          user_guid: user_guid,
        },
      });
      return response.data || [];
    },
    enabled: !!groupGuid && !!user_guid,
  });
};

export const useAddUserToIncident = (incidentId: string, groupGuid: string) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ incidentMembers }: AddUserToIncidentParams) => {
      const response = await axios(config.apiGateway.addUserToIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          incidentMembers,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('User added to incident successfully');
      if (teamsId) {
        dispatch(addMemberToTeams(incidentId, variables.memberEmails));
      }
    },
    onError: (error: any) => {
      if (error.response?.data === '{"ERROR":"User does not exist"}') {
        toast.error('User does not exist');
      } else if (
        error.response?.data === '{"ERROR":"User already in this Incident"}'
      ) {
        toast.error('User already in this Incident');
      } else {
        handleError(error, 'ERROR adding incident member');
      }
    },
  });
};

export const useRemoveUserFromIncident = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (user: IncidentMember) => {
      const response = await axios(
        config.apiGateway.removeUserFromIncidentByAdmin,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid: user.user_guid,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, user) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('User removed from incident successfully');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            null,
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'removeUserFromIncidentByAdminError');
    },
  });
};

export const useRemoveSelfFromIncident = (incidentId: string) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (user: IncidentMember) => {
      const response = await axios(config.apiGateway.removeSelfFromIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid: user.user_guid,
        },
      });
      return response.data;
    },
    onSuccess: (_response, user) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('Successfully left the incident');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            null,
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'removeSelfFromIncidentError');
    },
  });
};

export const useMarkIncidentUserActive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user_guid }: MarkIncidentUserActiveParams) => {
      const response = await axios(config.apiGateway.markIncidentMemberActive, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('User marked as active');
    },
    onError: (error: any) => {
      if (
        error.response?.data?.ERROR?.includes(
          'You can not work on two different incident at the same time.'
        )
      ) {
        toast.error(
          'User is already working on another incident. You can not work on two different incidents at the same time.',
          { autoClose: 10000 } as ToastOptions
        );
      } else {
        handleError(error, 'Failed to activate incident user');
      }
    },
  });
};

export const useMarkIncidentUserInactive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      user_guid,
      logout_time,
      minutes_worked,
      incident_member_id,
    }: {
      user_guid: string;
      logout_time: Date;
      minutes_worked: number;
      incident_member_id: string;
    }) => {
      const response = await axios(
        config.apiGateway.markIncidentMemberDeactive,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid,
            logout_time,
            minutes_worked,
            group_guid: groupGuid,
            incident_member_id,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [keys.incidentRoster, incidentId],
        (oldData: any) => {
          if (!oldData) return undefined;
          return oldData.map((member: any) => {
            if (member.incident_member_id === variables.incident_member_id) {
              return {
                ...member,
                active: 0,
                active_time: null,
              };
            }
            return member;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });

      toast.success('User marked as inactive');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to deactivate incident user');
    },
  });
};

export const useEditIncidentMember = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ member }: EditIncidentMemberParams) => {
      const response = await axios(
        config.apiGateway.editIncidentMemberByAdmin,
        {
          method: 'POST',
          data: {
            incidentMember: member,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentSchedule, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          keys.userIncidentSchedule,
          incidentId,
          variables.member.user_guid,
          variables.incident_member_id,
        ],
      });
      toast.success('Incident member updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to edit incident member');
    },
  });
};

export const useAddNonMemberToIncident = (groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentNonMember,
      incident_id,
    }: AddNonMemberToIncidentParams) => {
      const response = await axios(config.apiGateway.addNonMemberToIncident, {
        method: 'POST',
        data: {
          incident_id,
          incidentNonMember,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, variables.incident_id],
      });
      toast.success('Non-member added to incident successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to add non-member to incident');
    },
  });
};
